/**
 * CollectGoal (rules v2) golden marker: a gold frame around the tile's top face with a glint on
 * its corner, and a gold star with a thick ink rim that sits on the tile's bottom-left corner. The
 * star is the shape cue (colour-blind mode swaps items to patterned art, the marker does not need
 * to); in the bag only the star stays. Code-drawn like the card back: baked once per game.
 */
import type * as Phaser from 'phaser';
import { C, css } from '../game/theme.js';

export const GOLD_FRAME = 'fx_gold_frame';
export const GOLD_STAR = 'fx_gold_star';
const S = 128;
const T = 64;

function rounded(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Five-point star path centred on (cx, cy). */
export function starPoints(cx: number, cy: number, ro: number, ri: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (let k = 0; k < 10; k++) {
    const a = -Math.PI / 2 + (k * Math.PI) / 5;
    const r = k % 2 ? ri : ro;
    out.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  return out;
}

function gold(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number): CanvasGradient {
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  g.addColorStop(0, '#fff3b0');
  g.addColorStop(0.45, css(C.star));
  g.addColorStop(1, css(C.mustardDark));
  return g;
}

/** Bake the frame and star textures (idempotent). */
export function bakeGoldArt(scene: Phaser.Scene): void {
  if (scene.textures.exists(GOLD_FRAME)) return;
  // the frame: a gold ring over the tile's top face, ink on both edges, a white glint top-left
  {
    const c = document.createElement('canvas');
    c.width = S;
    c.height = S;
    const ctx = c.getContext('2d')!;
    const [o, i] = [4, 16];
    ctx.beginPath();
    rounded(ctx, o, o, S - 2 * o, S - 2 * o, 26);
    rounded(ctx, i, i, S - 2 * i, S - 2 * i, 15);
    ctx.fillStyle = gold(ctx, 0, 0, S, S);
    ctx.fill('evenodd');
    ctx.lineJoin = 'round';
    ctx.strokeStyle = css(C.ink);
    ctx.lineWidth = 5;
    ctx.beginPath();
    rounded(ctx, o, o, S - 2 * o, S - 2 * o, 26);
    ctx.stroke();
    ctx.lineWidth = 3;
    ctx.beginPath();
    rounded(ctx, i, i, S - 2 * i, S - 2 * i, 15);
    ctx.stroke();
    // the shine: a streak along the top edge and a four-point glint on the corner
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineCap = 'round';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(36, 10);
    ctx.lineTo(70, 10);
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    const [gx, gy, gr] = [14, 14, 11];
    ctx.moveTo(gx, gy - gr);
    ctx.quadraticCurveTo(gx, gy, gx + gr, gy);
    ctx.quadraticCurveTo(gx, gy, gx, gy + gr);
    ctx.quadraticCurveTo(gx, gy, gx - gr, gy);
    ctx.quadraticCurveTo(gx, gy, gx, gy - gr);
    ctx.fill();
    scene.textures.addCanvas(GOLD_FRAME, c);
  }
  // the star: gold, thick ink rim, a white highlight on its upper arm
  {
    const c = document.createElement('canvas');
    c.width = T;
    c.height = T;
    const ctx = c.getContext('2d')!;
    const pts = starPoints(T / 2, T / 2 + 2, T / 2 - 5, (T / 2 - 5) * 0.48);
    ctx.beginPath();
    pts.forEach((p, k) => (k ? ctx.lineTo(p.x, p.y + 3) : ctx.moveTo(p.x, p.y + 3)));
    ctx.closePath();
    ctx.fillStyle = 'rgba(74,44,42,0.35)';
    ctx.fill();
    ctx.beginPath();
    pts.forEach((p, k) => (k ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.closePath();
    ctx.fillStyle = gold(ctx, 0, 0, 0, T);
    ctx.fill();
    ctx.lineJoin = 'round';
    ctx.lineWidth = 5;
    ctx.strokeStyle = css(C.ink);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.beginPath();
    ctx.ellipse(T / 2 - 1, T / 2 - 12, 3, 6, -0.2, 0, Math.PI * 2);
    ctx.fill();
    scene.textures.addCanvas(GOLD_STAR, c);
  }
}
